import { HexColor, TerraDraw, TerraDrawLineStringMode, TerraDrawPolygonMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

import { GOOGLE_MAPS_SETTINGS } from '@/shared/constants';

/**
 * Names of the Terra Draw modes used throughout the fields map. Each name maps to a single
 * responsibility so the `finish` event handler can decide how to convert the drawn GeoJSON
 * feature into the matching native Google Maps overlay.
 */
export const TERRA_DRAW_MODES = {
  POLYGON_FIELD: 'polygon-field',
  POLYGON_MAPOBJECT: 'polygon-mapobject',
  LINESTRING_MAPOBJECT: 'linestring-mapobject',
  LINESTRING_SPLIT: 'linestring-split',
  LINESTRING_MEASURE: 'linestring-measure',
} as const;

export type TerraDrawModeName = typeof TERRA_DRAW_MODES[keyof typeof TERRA_DRAW_MODES];

export const SPLIT_LINE_COLOR = '#FF614C';

export interface CreateTerraDrawOptions {
  /**
   * Returns the current stroke/fill color used while drawing a map object. The color depends on
   * the currently selected map object type (isolation zone vs. multiplier group) which can change
   * at runtime, therefore it is resolved lazily on every render.
   */
  getMapObjectColor: () => string;
}

/**
 * Creates a configured Terra Draw instance for the fields map. Terra Draw replaces the deprecated
 * `google.maps.drawing.DrawingManager` and is only responsible for capturing the geometry. As soon
 * as a shape is finished the consumer converts the GeoJSON feature into a native Google Maps overlay
 * (see the `finish` handler in FieldsMap.vue) so all existing editing/splitting/measuring logic keeps
 * working unchanged.
 *
 * The adapter requires the map projection to be ready, so this must be called after the map has
 * emitted `projection_changed`. `draw.start()` is async - wait for the `ready` event before calling
 * `setMode`.
 */
export default function createTerraDraw(
  lib: typeof google.maps,
  map: google.maps.Map,
  options: CreateTerraDrawOptions,
): TerraDraw {
  const adapter = new TerraDrawGoogleMapsAdapter({ lib, map, coordinatePrecision: 9 });

  const mapObjectColor = (): HexColor => options.getMapObjectColor() as HexColor;

  const modes = [
    new TerraDrawPolygonMode({
      modeName: TERRA_DRAW_MODES.POLYGON_FIELD,
      styles: {
        fillColor: GOOGLE_MAPS_SETTINGS.POLYGON_ACTIVE_FILL_COLOR as HexColor,
        fillOpacity: 0.15,
        outlineColor: GOOGLE_MAPS_SETTINGS.POLYGON_ACTIVE_STROKE_COLOR as HexColor,
        outlineWidth: 3,
      },
    }),
    new TerraDrawPolygonMode({
      modeName: TERRA_DRAW_MODES.POLYGON_MAPOBJECT,
      styles: {
        fillColor: mapObjectColor,
        fillOpacity: 0,
        outlineColor: mapObjectColor,
        outlineWidth: 7,
      },
    }),
    new TerraDrawLineStringMode({
      modeName: TERRA_DRAW_MODES.LINESTRING_MAPOBJECT,
      styles: {
        lineStringColor: mapObjectColor,
        lineStringWidth: 7,
      },
    }),
    new TerraDrawLineStringMode({
      modeName: TERRA_DRAW_MODES.LINESTRING_SPLIT,
      styles: {
        lineStringColor: SPLIT_LINE_COLOR as HexColor,
        lineStringWidth: 3,
      },
    }),
    new TerraDrawLineStringMode({
      modeName: TERRA_DRAW_MODES.LINESTRING_MEASURE,
      styles: {
        lineStringColor: GOOGLE_MAPS_SETTINGS.MEASURE_DISTANCE_LINE_COLOR as HexColor,
        lineStringWidth: 3,
      },
    }),
  ];

  return new TerraDraw({ adapter, modes });
}
